import DataTableCard from '../../../components/ui/DataTableCard';
import {dateTimeBody} from '../../../utils/tableTemplates';
import React, {useState, useRef} from 'react';
import {Tag} from 'primereact/tag';
import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {InputTextarea} from 'primereact/inputtextarea';
import {Toast} from 'primereact/toast';
import {useMountEffect} from 'primereact/hooks';
import {getAllClaims, approveOrRejectClaim} from '../../../services/claims-service';
import Header from '../../../components/layout/headers/Header';
import DynamicNavigation from "../../../components/layout/navigation/DynamicNavigation";

const AdminClaimsList = () => {
    const [claims, setClaims] = useState([]);

    const [loading, setLoading] = useState(true);

    // Review Dialog States
    const toast = useRef(null);
    const [reviewDialog, setReviewDialog] = useState(false);
    const [selectedClaim, setSelectedClaim] = useState(null);
    const [reviewComment, setReviewComment] = useState('');
    const [reviewLoading, setReviewLoading] = useState(false);

    useMountEffect(() => {
        fetchAllClaims();
    });

    const fetchAllClaims = async () => {
        setLoading(true);
        try {
            const response = await getAllClaims();
            if (response.data?.status === "SUCCESS") {
                const rawClaims = response.data.responseBody || [];
                const mappedClaims = rawClaims.map(c => ({
                    claimId: c.referenceNumber || c.id,
                    status: c.claimStatus || c.status,
                    policyNumber: c.policyNumber,
                    incidentDate: c.deceased?.dateOfDeath || 'N/A',
                    dateSubmitted: c.createdAt ? new Date(c.createdAt).toLocaleDateString() : 'N/A',
                    referenceNumber: c.referenceNumber
                }));
                setClaims(mappedClaims);
            } else {
                setClaims([]);
            }
        } catch (error) {
            console.error('Error fetching all claims:', error);
            setClaims([]);
        } finally {
            setLoading(false);
        }
    };

    const statusBodyTemplate = (rowData) => {
        const val = rowData.status ? rowData.status.toLowerCase() : '';
        let severity = 'warning';
        if (val.includes('approved') || val.includes('paid')) severity = 'success';
        if (val.includes('reject') || val.includes('denied')) severity = 'danger';
        return <Tag value={rowData.status} severity={severity}/>;
    };

    const openReviewDialog = (claim) => {
        setSelectedClaim(claim);
        setReviewComment('');
        setReviewDialog(true);
    };

    const closeReviewDialog = () => {
        setReviewDialog(false);
        setSelectedClaim(null);
    };

    const submitReview = async (approve) => {
        if (!selectedClaim) return;
        setReviewLoading(true);
        try {
            const res = await approveOrRejectClaim(selectedClaim.referenceNumber, approve, reviewComment);
            if (res.data?.status === "SUCCESS") {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: res.data.message || 'Claim reviewed successfully',
                    life: 3000
                });
                closeReviewDialog();
                fetchAllClaims(); // refresh the list
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: res.data?.message || 'Failed to review claim',
                    life: 3000
                });
            }
        } catch (err) {
            console.error(err);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'An error occurred during review',
                life: 3000
            });
        } finally {
            setReviewLoading(false);
        }
    };



    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions-container flex items-center justify-center gap-2">
                <div className="tooltip" data-tip="View">
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={() => window.location.href = `/claims/view/${rowData.referenceNumber}`}
                    >
                        <i className="pi pi-eye text-lg"></i>
                    </button>
                </div>
                <div className="tooltip" data-tip="Review">
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={() => openReviewDialog(rowData)}
                    >
                        <i className="pi pi-file-edit text-lg"></i>
                    </button>
                </div>
            </div>
        );
    };

    const columns = [
        {field: 'claimId', header: 'Claim ID', sortable: true},
        {field: 'policyNumber', header: 'Policy Number', sortable: true},
        {field: 'incidentDate', header: 'Incident Date', body: dateTimeBody('incidentDate'), sortable: true},
        {field: 'dateSubmitted', header: 'Date Submitted', body: dateTimeBody('dateSubmitted'), sortable: true},
        {field: 'status', header: 'Status', body: statusBodyTemplate, sortable: true},
        {field: 'actions', body: actionBodyTemplate, style: {minWidth: '10rem'}},
    ];

    const reviewDialogFooter = (
        <React.Fragment>
            <Button className="btn btn-primary btn-outline" label="Cancel" icon="pi pi-times" outlined onClick={closeReviewDialog} disabled={reviewLoading}/>
            <Button className="btn btn-error text-white" label="Reject" icon="pi pi-times-circle" severity="danger" onClick={() => submitReview(false)}
                    disabled={reviewLoading}/>
            <Button className="btn btn-success text-white" label="Approve" icon="pi pi-check" severity="success" onClick={() => submitReview(true)}
                    disabled={reviewLoading}/>
        </React.Fragment>
    );

    return (
        <>
            <Header/>
            <DynamicNavigation/>
            <Toast ref={toast}/>
            <div className="content-body dl-dashboard">
                <div className="w-full px-4">
                    <DataTableCard
                        title="View All Claims"
                        exportName="Claims"
                        columns={columns}
                        rows={claims}
                        loading={loading}
                        rowsPerPage={10}
                        emptyMessage="No claims found"
                    />
                </div>
            </div>

            <Dialog visible={reviewDialog} style={{width: '450px'}} header="Review Claim" modal className="p-fluid"
                    footer={reviewDialogFooter} onHide={closeReviewDialog}>
                {selectedClaim && (
                    <div className="field">
                        <label htmlFor="comment" className="font-bold mb-3 d-block">Comment / Reason</label>
                        <InputTextarea id="comment" value={reviewComment}
                                       onChange={(e) => setReviewComment(e.target.value)} required rows={4} autoResize/>
                        <small className="d-block mt-2">Please provide a reason for the approval or rejection.</small>
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default AdminClaimsList;
